import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api, API } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

const ORIGIN = API.replace(/\/api$/, "");
const NEXT = { submitted: ["under_review"], under_review: ["revision_requested", "acknowledged"], revised_submission: ["under_review"],
  acknowledged: ["accepted", "superseded", "under_review"], accepted: ["superseded", "under_review"], revision_requested: ["under_review"] };
const LABEL = { under_review: "Start ERF internal review", revision_requested: "Request revision", acknowledged: "Acknowledge", accepted: "Accept for ERF project use", superseded: "Mark superseded" };

function Preview({ v, watermark }) {
  const [url, setUrl] = useState(null);
  const open = (variant) => api.get(`/phase3/architect/submission-files/${v.id}/signed-url`, { params: { variant } }).then((r) => setUrl({ href: `${ORIGIN}${r.data.url}`, variant }));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => open("original")} data-testid={`arch-sub-open-original-${v.id}`}>View original</Button>
        {v.preview && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => open("preview")} data-testid={`arch-sub-open-preview-${v.id}`}>View preview</Button>}
        {url && <a href={url.href} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center underline" data-testid={`arch-sub-download-${v.id}`}>Download (link expires in 5 minutes)</a>}
      </div>
      {url && (
        <div className="relative overflow-hidden rounded-md border border-erf-borderCream" data-testid={`arch-sub-viewer-${v.id}`}>
          {v.kind === "pdf" && url.variant === "original" ? <iframe title={`Revision ${v.revision_number}`} src={url.href} className="h-[480px] w-full" /> : <img src={url.href} alt={`Revision ${v.revision_number}`} className="max-h-[480px] w-full object-contain" />}
          <span className="pointer-events-none absolute left-2 top-2 rounded bg-erf-dark/85 px-2 py-1 text-xs text-[#FEFCF7]" data-testid={`arch-sub-watermark-${v.id}`}>{watermark}{url.variant === "preview" ? " · preview copy" : ""}</span>
        </div>
      )}
    </div>
  );
}

export default function SubmissionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase3/architect/submissions/${id}`).then((r) => setS(r.data)).catch((e) => setError(errMsg(e, "Not found."))), [id]);
  useEffect(() => { load(); }, [load]);
  const run = (p) => p.then(() => { setError(null); setText(""); load(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  const upload = (path) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    run(api.post(path, file, { params: { filename: file.name }, headers: { "Content-Type": "application/octet-stream" } })).finally(() => { setBusy(false); e.target.value = ""; });
  };
  const mine = s && s.architect_id === user?.id;
  return (
    <AdminAppShell activeNav="architect-portal" user={user} title={s ? s.title : "Submission"} subtitle={s ? s.state_label : ""}
      breadcrumbs={[{ label: "Architect Portal", to: "/phase3/architect/portal" }, { label: "Submission", to: null }]}>
      <div data-testid="arch-sub-detail" className="space-y-5 text-sm font-nav text-erf-dark">
        {error && <p role="alert" className="text-[#991B1B]" data-testid="arch-sub-error">{error}</p>}
        {s && (
          <>
            <div role="note" className="rounded-xl border border-[#B45309]/40 bg-[#FFF7E6] px-4 py-3 text-[#5C3A06]" data-testid="arch-sub-disclaimer">{s.disclaimer}</div>
            <Card className="ds-card border-erf-borderCream"><CardContent className="p-5 space-y-3">
              <p data-testid="arch-sub-state">Status: <strong>{s.state_label}</strong> · revision {s.revision_number}{s.drawing_number && ` · ${s.drawing_number}`}</p>
              {mine && ["draft", "revision_requested"].includes(s.state) && (
                <label className="inline-flex h-11 cursor-pointer items-center rounded-md bg-erf-gold px-4 text-erf-dark">{busy ? "Uploading…" : s.state === "draft" ? "Upload drawing (PDF or image)" : "Upload revised drawing"}
                  <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic" className="sr-only" disabled={busy} onChange={upload(`/phase3/architect/submissions/${id}/revisions`)} data-testid="arch-sub-upload" />
                </label>
              )}
              {mine && s.state === "draft" && s.current_version_id && <Button className="ml-2 h-11 bg-erf-dark text-[#FEFCF7]" onClick={() => run(api.post(`/phase3/architect/submissions/${id}/submit`))} data-testid="arch-sub-submit">Submit to ERF</Button>}
              {mine && (
                <label className="ml-2 inline-flex h-11 cursor-pointer items-center rounded-md border border-erf-borderCream bg-white px-4">Add supporting document
                  <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic" className="sr-only" onChange={upload(`/phase3/architect/submissions/${id}/attachments`)} data-testid="arch-sub-attach" />
                </label>
              )}
              {s.role === "manager" && !mine && (NEXT[s.state] || []).length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="arch-sub-transitions">
                  {NEXT[s.state].map((to) => <Button key={to} variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase3/architect/submissions/${id}/transition`, { to, reason: text }))} data-testid={`arch-sub-to-${to}`}>{LABEL[to]}</Button>)}
                </div>
              )}
              <label className="block">{s.role === "manager" ? "Comment, correction request or reason (at least 10 characters for revision requests and moving backward)" : "Response to ERF"}
                <Input value={text} onChange={(e) => setText(e.target.value)} className="mt-1 h-11" data-testid="arch-sub-text" />
              </label>
              <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => run(api.post(`/phase3/architect/submissions/${id}/responses`, { body: text, version_id: s.current_version_id }))} data-testid="arch-sub-respond">Post response</Button>
            </CardContent></Card>
            <Card className="ds-card border-erf-borderCream"><CardContent className="p-5 space-y-4">
              <h2 className="font-display font-semibold text-base md:text-lg">Revisions</h2>
              {s.versions.length === 0 && <p>No files yet.</p>}
              {s.versions.map((v) => (
                <div key={v.id} className="space-y-1" data-testid={`arch-sub-version-${v.revision_number}`}>
                  <p><strong>Revision {v.revision_number}</strong> · {v.original_filename} · {(v.byte_size / 1024).toFixed(0)} KB · {v.superseded ? "superseded" : "current"} · SHA-256 {v.sha256.slice(0, 12)}…</p>
                  <Preview v={v} watermark={s.preview_watermark} />
                </div>
              ))}
              {s.attachments.length > 0 && <><h3 className="font-semibold">Supporting documents</h3><ul>{s.attachments.map((a) => <li key={a.id}>{a.original_filename}</li>)}</ul></>}
            </CardContent></Card>
            <Card className="ds-card border-erf-borderCream"><CardContent className="p-5">
              <h2 className="font-display font-semibold text-base md:text-lg">Responses and history</h2>
              <ul className="mt-2 space-y-1" data-testid="arch-sub-responses">
                {s.responses.map((m) => <li key={m.id}><strong>{m.kind.replace("_", " ")}</strong>: {m.body}</li>)}
                {s.history.map((h, i) => <li key={`h${i}`} className="text-erf-dark/80">{h.from} → {h.to}{h.reason ? ` — ${h.reason}` : ""}</li>)}
              </ul>
            </CardContent></Card>
          </>
        )}
      </div>
    </AdminAppShell>
  );
}
